// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { Fragment, useState } from 'react';
import { bool, func, string, object, number, arrayOf } from 'prop-types';
import moment from 'moment';
import { find, get } from 'lodash';
import {
  Chip,
  Collapse,
  Dialog,
  IconButton,
  TableCell,
  TableRow,
  Tooltip,
} from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import CertificateIcon from '../../../icons/certificate.svg';
import ExternalAuthIcon from '../../../icons/externalauth.svg';
import KeyIcon from '../../../icons/key.svg';
import clsx from 'clsx';

import EditKey from '../../EditKey';
import SingleKeyView from '../SingleKeyView';
import { getKeyStatus } from '../../utils';
import { getI18nFormattedMessage } from '../../../../../utils/intl';
import { KEY_STATUS_DELETE_FAILED } from '../../labels';

const SingleRow = ({
  row,
  idx,
  classes,
  notifyMessages,
  applicationDetails,
  appSecretHashingMetadata,
  applicationUuid,
  isLocking,
  isApiKeyExpiryEnabled,
  startReportingDays,
  urgentReportingDays,
  locked,
  disabledByInternal,
  expandIndex,
  setExpandIndex,
  onSaveDone,
  deleteKey,
  deleteOAuthClient,
  regenerateSecret,
  registerKey,
  regeneratedSecretDetails,
  regerateSecretStatus,
  registeredKeyDetails,
  registerKeyStatus,
  registerKeyError,
  isAdmin,
  isEditDisabled,
  defaultHashType,
  isPortalAdmin,
  fetchApplicationKeys,
  earliestExpiryCertTs,
  certificateList,
  authProviders,
  user,
  canAddKey,
}) => {

  const [isEditKeyOpen, setIsEditKeyOpen] = useState(false);

  const getAuthenticationInfo = () => {
    const authProvider = find(authProviders, item => item.uuid === row.authProviderUuid);
    return authProvider && authProvider.name;
  };

  const getExpiryStatus = () => {
    const authMethod = get(row, 'authMethod');
    const secretExpiryTsInMS = authMethod === 'SECRET' ? get(row, 'secretExpiryTs') : earliestExpiryCertTs;
    const status = get(row, 'status');
    const keyId = get(row, 'apiKey');
    if (!secretExpiryTsInMS || (authMethod === 'NONE')) {
      return (<span />)
    }
    // const secretExpiryTsInMS = 1682009761000;
    const expiryTime = moment(secretExpiryTsInMS);
    const currentTime = moment();
    const days = expiryTime.diff(currentTime, 'days');
    let className = classes.normalKey;
    let label = '';
    // string to represent status, which is used by automation
    let expiryStatus;
    if (status === 'EXPIRED' || status === 'DISABLED') {
      label = '';
      className = '';
      expiryStatus = 'none';
    } else {
      label = days;
      if (days < urgentReportingDays) {
        className = classes.urgentReportingKey;
        expiryStatus = 'urgentReporting';
        if (days < 0) {
          label = 0;
        }
      } else if (days < startReportingDays) {
        className = classes.startReportingKey;
        expiryStatus = 'startReporting';
      } else {
        className = classes.normalKey;
        expiryStatus = 'normal';
      }
    }

    return (
    <span
      data-apim-test={`key-${keyId}-expiry-status-${expiryStatus}`}
      className={clsx(classes.status, className)}
    >
      {label}
    </span>);
  };

  const getAuthMethodIcon = (authMethod) => {
    if (authMethod === 'SECRET') {
      return KeyIcon;
    } else if (authMethod === 'CERTIFICATE') {
      return CertificateIcon;
    }
    return ExternalAuthIcon;
  };
  const onRegistrationDialogClose = () => {
    onSaveDone();
    fetchApplicationKeys(applicationUuid);
  }
  return (
    <Fragment>
      <Dialog
        open={isEditKeyOpen}
        onClose={() => setIsEditKeyOpen(false)}
        aria-labelledby="edit-key-dialog"
        id={row.apiKey}
        classes={{
          paper: classes.paperClass,
        }}
      >
        <EditKey
          applicationDetails={applicationDetails}
          applicationUuid={applicationUuid}
          authProviders={authProviders}
          details={row}
          closeAction={() => {
            setIsEditKeyOpen(false);
            fetchApplicationKeys(applicationUuid);
          }}
          appSecretHashingMetadata={appSecretHashingMetadata}
          notifyMessages={notifyMessages}
          isLocking={isLocking}
          onSaveDone={onSaveDone}
          isAdmin={isAdmin}
          isPortalAdmin={isPortalAdmin}
          user={user}
          editable={!isEditDisabled}
        />
      </Dialog>
      <TableRow
        data-apim-test={`${row.apiKey}-single-row`}
        onClick={() => setExpandIndex(expandIndex === idx ? -1 : idx)}
      >
        <TableCell component="th" scope="row" className={classes.flexBlock}>
          <img src={getAuthMethodIcon(get(row, 'authMethod'))}
            className={classes.certOrKeyIcon}
          />
          <span className={classes.keyListKeyName}>
            {row.name}
          </span>
          {get(row, 'defaultKey') &&
            <Chip
              className={classes.defaultChip}
              size="small"
              label={'Default'}
            />
          }
        </TableCell>
        <TableCell component="th" scope="row">
          {getAuthenticationInfo()}
        </TableCell>
        <TableCell component="th" scope="row">
          {getKeyStatus(get(row, 'status'), classes)}
          {get(row, 'status') === KEY_STATUS_DELETE_FAILED &&
            <Tooltip
              arrow disableFocusListener disableTouchListener placement="top"
              title={getI18nFormattedMessage('label.application.key.delete.failed.status.tooltip')}
            >
              <span className={classes.helpIcon}>{getI18nFormattedMessage('label.more.info')}</span>
            </Tooltip>
          }
        </TableCell>
        <TableCell component="th" scope="row">
          {getExpiryStatus()}
        </TableCell>
        <TableCell component="th">
          <div id={`expand-collapse-button-${idx}`}>
            <IconButton
            className={classes.expandCollapseButton}
            size="small"
            onClick={() => setExpandIndex(expandIndex === idx ? -1 : idx)}>
              {expandIndex===idx ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </div>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ padding: 0 }} colSpan={6}>
          <Collapse in={expandIndex === idx} timeout="auto" unmountOnExit>
            <div
              className={classes.singleKeyViewCell}
              data-apim-test={`${row.apiKey}-expanded-row`}
            >
              <SingleKeyView
                applicationDetails={applicationDetails}
                idx={idx}
                isApiKeyExpiryEnabled={isApiKeyExpiryEnabled}
                keyDetails={row}
                classes={classes}
                notifyMessages={notifyMessages}
                setIsEditKeyOpen={setIsEditKeyOpen}
                isLocking={isLocking}
                locked={locked}
                disabledByInternal={disabledByInternal}
                appSecretHashingMetadata={appSecretHashingMetadata}
                authProviders={authProviders}
                onSaveDone={onSaveDone}
                deleteKey={deleteKey}
                deleteOAuthClient={deleteOAuthClient}
                regenerateSecret={regenerateSecret}
                registerKey={registerKey}
                onRegistrationDialogClose={onRegistrationDialogClose}
                applicationUuid={applicationUuid}
                regeneratedSecretDetails={regeneratedSecretDetails}
                regerateSecretStatus={regerateSecretStatus}
                registeredKeyDetails={registeredKeyDetails}
                registerKeyStatus={registerKeyStatus}
                registerKeyError={registerKeyError}
                defaultHashType={defaultHashType}
                isEditDisabled={isEditDisabled}
                isPortalAdmin={isPortalAdmin}
                isAdmin={isAdmin}
                earliestExpiryCertTs={earliestExpiryCertTs}
                certificateList={certificateList}
                startReportingDays={startReportingDays}
                urgentReportingDays={urgentReportingDays}
                user={user}
                canAddKey={canAddKey}
              />
            </div>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
};

SingleRow.propTypes = {
  row: object,
  idx: number,
  classes: object,
  notifyMessages: func,
  isAdmin: bool,
  applicationDetails: object,
  appSecretHashingMetadata: object,
  applicationUuid: string,
  isLocking: bool,
  isApiKeyExpiryEnabled: bool,
  startReportingDays: number,
  urgentReportingDays: number,
  locked: bool,
  disabledByInternal: bool,
  expandIndex: number,
  setExpandIndex: func,
  regeneratedSecretDetails: object,
  regerateSecretStatus: string,
  registeredKeyDetails: object,
  registerKeyStatus: string,
  registerKeyError: string,
  deleteKey: func,
  deleteOAuthClient: func,
  regenerateSecret: func,
  registerKey: func,
  onRegistrationDialogClose: func,
  onSaveDone: func,
  defaultHashType: string,
  isEditDisabled:bool,
  fetchApplicationKeys: func,
  isPortalAdmin: bool,
  earliestExpiryCert: number,
  certificateList: arrayOf(object),
  earliestExpiryCertTs: number,
  authProviders: arrayOf(object),
  intl: object,
  user: object,
  canAddKey: bool,
};

export default SingleRow;